'use client';

import { createClient } from '@/lib/supabase/client';

export interface SavedCalculation {
  id: string;
  userId: string;
  calculatorType: string;
  calculationName: string | null;
  inputs: any;
  results: any;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

function isSchemaError(error: any): boolean {
  if (!error) return false;
  
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ];
    return schemaErrorPatterns.some(pattern => pattern.test(error.message));
  }
  
  return false;
}

export const calculationService = {
  async getAll(userId: string): Promise<SavedCalculation[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('saved_calculations')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    
    if (error) {
      if (isSchemaError(error)) {
        throw error;
      }
      console.error('Data error:', error.message);
      return [];
    }
    
    return data?.map(row => ({
      id: row.id,
      userId: row.user_id,
      calculatorType: row.calculator_type,
      calculationName: row.calculation_name,
      inputs: row.inputs,
      results: row.results,
      notes: row.notes,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    })) || [];
  },

  async getByType(userId: string, calculatorType: string): Promise<SavedCalculation[]> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('saved_calculations')
      .select('*')
      .eq('user_id', userId)
      .eq('calculator_type', calculatorType)
      .order('created_at', { ascending: false });
    
    if (error) {
      if (isSchemaError(error)) {
        throw error;
      }
      console.error('Data error:', error.message);
      return [];
    }
    
    return data?.map(row => ({
      id: row.id,
      userId: row.user_id,
      calculatorType: row.calculator_type,
      calculationName: row.calculation_name,
      inputs: row.inputs,
      results: row.results,
      notes: row.notes,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    })) || [];
  },

  async create(calculation: {
    userId: string;
    calculatorType: string;
    calculationName?: string;
    inputs: any;
    results: any;
    notes?: string;
  }): Promise<SavedCalculation> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('saved_calculations')
      .insert({
        user_id: calculation.userId,
        calculator_type: calculation.calculatorType,
        calculation_name: calculation.calculationName || null,
        inputs: calculation.inputs,
        results: calculation.results,
        notes: calculation.notes || null,
      })
      .select()
      .single();
    
    if (error) throw error;
    
    return {
      id: data.id,
      userId: data.user_id,
      calculatorType: data.calculator_type,
      calculationName: data.calculation_name,
      inputs: data.inputs,
      results: data.results,
      notes: data.notes,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  async update(id: string, updates: {
    calculationName?: string;
    inputs?: any;
    results?: any;
    notes?: string;
  }): Promise<SavedCalculation> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const updateData: any = {};
    if (updates.calculationName !== undefined) updateData.calculation_name = updates.calculationName;
    if (updates.inputs !== undefined) updateData.inputs = updates.inputs;
    if (updates.results !== undefined) updateData.results = updates.results;
    if (updates.notes !== undefined) updateData.notes = updates.notes;

    const { data, error } = await supabase
      .from('saved_calculations')
      .update(updateData)
      .eq('id', id)
      .eq('user_id', user.id)
      .select()
      .single();
    
    if (error) throw error;
    
    return {
      id: data.id,
      userId: data.user_id,
      calculatorType: data.calculator_type,
      calculationName: data.calculation_name,
      inputs: data.inputs,
      results: data.results,
      notes: data.notes,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  async delete(id: string): Promise<void> {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase
      .from('saved_calculations')
      .delete()
      .eq('id', id)
      .eq('user_id', user.id);
    
    if (error) throw error;
  },
};